import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ChangeDetectorRef } from '@angular/core';
import filter from 'lodash-es/filter';
import { Ng2DeviceService } from 'ng2-device-detector';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs/Subscription';
import { environment } from '../../../environments/environment';
import { AppMessageService } from '../../app-message.service';
import { TranslateParam, DynamicRoute } from '../../common/interfaces';
import { ProductService } from '../../common/services/product.service';
import { ImageType } from '../../products/enums/image-type.enum';
import { Categories } from '../../products/interfaces/categories.interface';
import { Product } from '../../products/interfaces/product.interface';
import { CacheKey } from '../constants/cachekey.constants';
import { DynamicPageSlugNames } from '../constants/dynamic-page-slug.constant';
import { MemberLevel } from '../enums/member-level.enum';
import { PurchaseFlow } from '../enums/purchase-flow.enum';
import { MenuItem } from '../interfaces/menu-item.interface';
import { StoreConfig } from '../interfaces/store-config.interface';
import { ActiveSessionService } from '../services/active-session.service';
import { CacheService } from '../services/cache.service';
import { CommonService } from '../services/common.service';
import { ConfigurationService } from '../services/configuration.service';
import { ReportOnlyCountryService } from '../services/report-only-country.service';

@Component({
  selector: 'mega-menu',
  templateUrl: '../templates/template1/views/mega-menu.component.html',
  styleUrls: [
    '../templates/template1/themes/default/less/mega-menu.component.less'
  ], animations: [
    trigger('slideInOut', [
      state('in', style({
        overflow: 'hidden',
        height: '*',
        opacity: '1'
      })),
      state('out', style({
        opacity: '0',
        overflow: 'hidden',
        height: '0px'
      })),
      transition('in <=> out', animate('400ms ease-in-out'))
    ])
  ],
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class MegaMenuComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  store: StoreConfig;
  storeId: number;
  languageCode: string;
  countryCode: string;
  memberTitle = MemberLevel;
  memberTitleId: number = MemberLevel.RETAIL;
  imageTypes: any = ImageType;
  purchaseFlow: PurchaseFlow;
  categories: Categories[];
  catalogs: Categories[];
  featuredProducts: any[] = [];
  products: Product[] = [];
  hoveredCategory: Categories;
  selected = -1;
  s3BucketUrl = environment.cdnURL;
  cookieService: CookieService = new CookieService(document);
  cardsContent: any[];
  siteMapId: number;
  isTouch = false;
  translateParams: TranslateParam;
  appSites: MenuItem[];
  siteMapsSubscription: Subscription;
  loadSiteMaps = false;
  productHoverTimer: any;

  isFocused = false;
  @ViewChild('lnkCategory', { static: false }) public _inputElement: ElementRef;
  @Output("escapeClicked") public escapeClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  constructor(
    private _configurationService: ConfigurationService,
    private _cacheService: CacheService,
    private _commonService: CommonService,
    private _deviceService: Ng2DeviceService,
    private _productService: ProductService,
    private _appMessageService: AppMessageService,
    private _activeSessionService: ActiveSessionService,
    private _reportOnlyCountryService: ReportOnlyCountryService,
    private renderer: Renderer2,
    private changeDetectRef: ChangeDetectorRef
  ) { }
  ngOnInit(): void {
    this.languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    this.countryCode = this._cacheService.getCookieValue(CacheKey.countryCode);
    this.getDeviceSpecification();
    this.getStoreData();
    this.memberTitleId = this._activeSessionService.getMemberTitleId();    // All Categories
    this.purchaseFlow = this._commonService.getPurchaseFlowType();
  }

  /**
   * This method used for device specification
   * getDeviceSpecification
   * @memberof MegaMenuComponent
   */
  getDeviceSpecification() {
    const accessDeviceInfo = this._deviceService.getDeviceInfo();
    if (accessDeviceInfo) {
      if (!this._deviceService.isDesktop()) {
        this.isTouch = true;
      }
    }
  }


  getCardsContent(sitemapId: number) {
    this._commonService.getCardsContentBySitemapId(sitemapId).subscribe(response => {
      if (response) {
        this.cardsContent = response;
        const cards: any = document.querySelectorAll('div[data-t-card]');
        if (cards) {
          for (const card in cards) {
            if (Number(card) >= 0) {
              const temp = cards[card].getAttribute('data-t-card');
              if (this.cardsContent) {
                // tslint:disable-next-line:forin
                for (const place in this.cardsContent) {
                  const placeholder = this.cardsContent[place]
                  if (placeholder.indexId === temp) {
                    if (placeholder.cardContent !== '') {
                      cards[card].innerHTML = placeholder.cardContent;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }, (error: any) => {
      this.translateParams = this._commonService.handleError(error);
    });
  }

  /**
 * @description
 * @date 2019-09-11
 * @private
 * @memberof MegaMenuComponent
 */
  private getSiteMapsSubject(): void {
    this.siteMapsSubscription = this._appMessageService.getSiteMapsSubject().subscribe(x => {
      if (this.loadSiteMaps) {
        this.loadSiteMaps = false;
        this.getMenus();
      }
    });
  }

  getStoreData() {
    const storeConfig: StoreConfig = this._configurationService.getStoreData();
    if (storeConfig != null) {
      this.store = storeConfig;
      this.storeId = this.store.id;
      // All Categories
      this.getCategories();
      // Custom Categories
      this.getAllCatalogs();
      this.getMenus();
      this.getSiteMapsSubject();
    }
  }

  /**
   * @description This method used to get shop header sitemap id
   * getMenus()
   * @memberof MegaMenuComponent
   */
  getMenus(): void {
    const menus = this._cacheService.get(CacheKey.SiteMaps);
    // shop header dynamic page
    if (menus && menus.length) {
      const shopHeader: MenuItem = menus.find(x => x.slug === DynamicPageSlugNames.SHOP_HEADER);
      if (shopHeader) {
        const siteMapId = shopHeader.siteMapId;
        if (siteMapId) {
          this.getCardsContent(siteMapId);
        }
      }
    } else {
      this.loadSiteMaps = true;
    }
  }

  /**
   * to get all categories
   */
  getCategories(): void {
    const storeId: number = this.store.id;
    let titanPlatformEnabled = true;
    if ('titanPlatformEnabled' in this.store) {
      titanPlatformEnabled = (this.store as any).titanPlatformEnabled;
    }
    const groupId = 1;
    if (storeId && !this._reportOnlyCountryService.reportOnlyCountry(this.countryCode) && titanPlatformEnabled) {
      this._commonService
        .getCategories(storeId, groupId, this.languageCode)
        .subscribe(response => {
          if (response && response.length > 0) {
            this.categories = response;
          }
          // this.changeDetectRef.detectChanges();
        }, (error: any) => {
          this.translateParams = this._commonService.handleError(error);
        });
    }
  }

  showProductsOnMobile(category: any, index: number) {
    if (index === this.selected && window.innerWidth < 768) {
      this.selected = -1;
      return;
    }
    // changes for goto category
    this.getProductsByCategoryId(category, true, true, index);
  }

  /**
     * Click on Shop All link goto the product category page
     * @param  {Event} event
     * @returns void
     */
  gotoShopCategoryPage(): void {
    if (this.hoveredCategory) {
      this.getProductsByCategoryId(this.hoveredCategory, true, true);
    }
  }
  /**
   * get all Products based on category on (mouseover|click)
   * @param  {any} category
   * @param  {boolean} isLoadCategoryProducts?
   * @returns void
   */
  getProductsByCategoryId(category: any, isLoadCategoryProducts?: boolean, viewAll?: boolean, categoryIndex?: number): void {
    this.hoveredCategory = category;
    if (isLoadCategoryProducts) {
      let redirectURL = environment.shopURL + '/' + this.countryCode.toLowerCase() + '/' + this.languageCode.toLowerCase();
      if (category && category.slug) {
        redirectURL = redirectURL + '/' + category.slug;
      } else {
        redirectURL = redirectURL + '/products';
      }
      this._cacheService.setCookieValue(CacheKey.SelectedCategoryId, category.productCategoryId);
      redirectURL = this._commonService.appendFBOID(redirectURL);
      window.location.href = redirectURL;
      return;
    }
    const categoryId = category.productCategoryId;
    const pageNumber = 1;
    const pageSize = 10;
    // on click of any category, this will close the opened category products
    this.selected = -1;
    clearTimeout(this.productHoverTimer);
    this.productHoverTimer = setTimeout(() => {
      if (this.storeId && categoryId) {
        // load catalog products when hover
        if (category.isCustomCatalog) {
          this._productService
            .getProductsByCatalogId(this.storeId, categoryId, this.languageCode,
              pageSize, pageNumber, '')
            .subscribe((response: Product[]) => {
              this.products = [];
              response = response || [];
              if (response) {
                this.products = response;
                this.filterFeaturedProducts(response);
                // on click of any category, this will open the category products
                // this.selected = categoryIndex;
              }
            });
        } else {
          // load category products when hover
          this._productService
            .getProductByCategoryId(categoryId, this.storeId, this.languageCode, pageSize, pageNumber, '')
            .subscribe((response: Product[]) => {
              this.products = [];
              response = response || [];
              if (response) {
                this.products = response;
                this.filterFeaturedProducts(response);
                // on click of any category, this will open the category products
                // this.selected = categoryIndex;
              }
            }, (error: any) => {
              this.translateParams = this._commonService.handleError(error);
            });
        }
      }
    }, 100);
    this.selected = categoryIndex;
  }

  /**
   * goto detail page in shop
   * @param  {string} slug
   * @returns void
   */
  gotoProductDetail(product: Product): void {
    let url;
    let selectedCategory;
    if (product.categories) {
      selectedCategory = product.categories[0];
    }
    if (selectedCategory && selectedCategory.slug) {
      url = environment.shopURL + '/' + this.countryCode.toLowerCase() + '/' + this.languageCode.toLowerCase() + '/products/' +
        selectedCategory.slug + '/' + product.slug;
      this._cacheService.setCookieValue(CacheKey.SelectedCategoryId, this.hoveredCategory.productCategoryId);
    } else {
      url = environment.shopURL + '/' + this.countryCode.toLowerCase() + '/' + this.languageCode.toLowerCase() + '/products/' +
        product.slug;
      this._cacheService.setCookieValue(CacheKey.SelectedCategoryId, this.hoveredCategory.productCategoryId);
    }
    url = this._commonService.appendFBOID(url);
    const cookieBannerVal = this._cacheService.get(CacheKey.CookieBanner);
    if (cookieBannerVal) {
      url = url + '&showCookie=' + cookieBannerVal;
    }
    window.location.href = url;
  }
  /**
   * get all the Catalogs
   * @returns void
   */
  getAllCatalogs(): void {
    if (this.storeId) {
      this._productService
        .getCustomCatalogs(this.storeId)
        .subscribe((catalogs: any) => {
          if (catalogs && catalogs.length > 0 && catalogs[0].id) {
            this.catalogs = catalogs;
            this.catalogs = this.getCatalogArray(); // filterling / changing the catalog properties
            this.getProductsByCategoryId(this.catalogs[0], false);
            // this.changeDetectRef.detectChanges();
          } else {
            if (this.categories) {
              this.getProductsByCategoryId(this.categories[0]);
            }
          }
        }, (error: any) => {
          this.translateParams = this._commonService.handleError(error);
        });
    }
  }

  private filterFeaturedProducts(products: Product[]): void {
    this.featuredProducts = filter(products, function (product) {
      return product.display.featuredProduct;
    }).slice(0, 6);
  }

  private getCatalogArray(): any[] {
    return this.catalogs.map(function (x: any) {
      return {
        productCategoryId: x.id,
        id: x.id,
        parentCategoryConfigId: null,
        categoryName: x.name,
        storeId: x.storeId,
        isCustomCatalog: true,
        slug: x.slug,
        description: x.description
      };
    });
  }

  /**
   * @description Navigate to portal
   * @author njagadeesan
   * @date 2020-01-11
   * @param {DynamicRoute} dynamicRoute
   * @memberof MegaMenuComponent
   */
  navigateToPortal(dynamicRoute: DynamicRoute): void {
    let route = dynamicRoute.route;
    if (route) {
      if (environment.betaPortal) {
        route = this._commonService.constructBetaUrl(route);
      }
      window.open(route, dynamicRoute.target);
    }
  }

  setCollapse(isCollapse: boolean): void {
    this._appMessageService.setCollapseMenu(isCollapse);
  }

  ngOnDestroy(): void {
    if (this.siteMapsSubscription) {
      this.siteMapsSubscription.unsubscribe();
    }
  }
  toogleDisplayCSS(collapsed: boolean) {
    let displayMode = collapsed ? 'block' : 'none';
    if (collapsed === true) {
      this.focusFirstElement();
    }
    else {
      this.isFocused = false;
    }
    return {
      'display': displayMode
    };
  }

  focusFirstElement() {
    if (this.isFocused === false) {
      setTimeout(() => {
        let inputField: HTMLElement = <HTMLElement>document.querySelectorAll('#lnkCategory')[0];
        inputField && inputField.focus();
      }, 50);
      this.isFocused = true;
    }
  }

  onEscape() {
    this.escapeClicked.emit(true);
  }
}
